import { clsx } from 'clsx';
import { DataTable, type DataTableColumn } from '../../_shared/DataTable';
import { useCampaignPersonas } from '../hooks/useCampaignPersonas';
import { NUMBER, PERCENT } from '../../_shared/format';
import type { PerformanceCampaignPersonaRow } from '@/services/api';

export function CampaignPersonasTab({ campaignId }: { campaignId: string }) {
  const { data, loading } = useCampaignPersonas(campaignId, true);

  const columns: DataTableColumn<PerformanceCampaignPersonaRow>[] = [
    {
      key: 'persona_name',
      label: 'Persona',
      render: (row) =>
        row.persona_name ? (
          <span className="font-medium text-navy-900">{row.persona_name}</span>
        ) : (
          <span
            className="text-slate-400 italic"
            title="Visitors with no resolved HEM/persona match"
          >
            Unresolved
          </span>
        ),
      className: undefined,
    },
    {
      key: 'visitors',
      label: 'Visitors',
      align: 'right',
      width: '110px',
      render: (row) => NUMBER.format(row.visitors),
    },
    {
      key: 'identified_visitors',
      label: 'Identified',
      align: 'right',
      width: '100px',
      render: (row) => NUMBER.format(row.identified_visitors),
    },
    {
      key: 'pageviewers',
      label: 'Pageviews',
      align: 'right',
      width: '100px',
      render: (row) => NUMBER.format(row.pageviewers),
    },
    {
      key: 'cart_adders',
      label: 'Cart Adds',
      align: 'right',
      width: '100px',
      render: (row) => NUMBER.format(row.cart_adders),
    },
    {
      key: 'share_pct',
      label: 'Share',
      align: 'right',
      width: '90px',
      render: (row) => PERCENT(row.share_pct, 1),
    },
  ];

  return (
    <div>
      {data ? (
        <div className="mb-3 flex items-center gap-3 text-xs text-slate-500">
          <span>
            Total visitors:{' '}
            <strong className="text-navy-900">
              {NUMBER.format(data.total_visitors)}
            </strong>
          </span>
        </div>
      ) : null}
      <DataTable<PerformanceCampaignPersonaRow>
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(r) => `${r.persona_name ?? 'unresolved'}_${r.visitors}`}
        loading={loading}
        emptyText="No persona attribution available for this campaign."
      />
      {data && data.rows.some((r) => r.persona_name === null) ? (
        <p
          className={clsx(
            'mt-2 text-[11px] text-slate-400',
            'rounded border border-slate-200 bg-slate-50 px-3 py-2',
          )}
        >
          "Unresolved" rows are visitors without a HEM-resolved persona. This
          gap closes as identity resolution coverage improves.
        </p>
      ) : null}
    </div>
  );
}
